<template>
  <!-- Fixed App Header (white, subtle ring/shadow, consistent with new cards) -->
  <header class="fixed top-0 left-0 right-0 z-[1001] bg-white border-b border-gray-200 shadow-sm">
    <div class="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-2 md:py-3">
      <div class="flex items-center justify-between">
        <!-- Brand -->
        <router-link to="/" class="flex items-center gap-2 sm:gap-3 group">
          <img
            src="@/assets/logo.webp"
            alt="Logo"
            class="h-6 w-auto sm:h-7 transition-transform duration-300 group-hover:scale-110"
          />
          <h1
            class="text-gray-900 text-lg sm:text-xl font-bold tracking-tight group-hover:text-primary transition-colors duration-200"
          >
            {{ appName }}
          </h1>
        </router-link>

        <!-- Desktop Navigation -->
        <nav class="hidden lg:flex items-center gap-5 xl:gap-8">
          <template v-if="!loggedIn">
            <router-link
              v-for="item in desktopGuestNavInternal"
              :key="item.path"
              :to="item.path"
              class="text-sm font-medium transition-colors"
              :class="item.path === '/ai-youtube-video-ideas-generator' && isHomeActive
                ? 'text-primary'
                : item.path === '/pricing' && isPricingActive
                  ? 'text-primary'
                  : 'text-gray-700 hover:text-gray-900'"
            >
              <span class="inline-flex items-center gap-1.5">
                <span class="material-symbols-outlined text-base text-gray-400 group-hover:text-primary/80 hidden xl:inline">
                  chevron_right
                </span>
                {{ item.label }}
              </span>
            </router-link>

            <!-- Use computed property for Contact link -->
            <a
              v-for="item in desktopGuestNavContact"
              :key="item.path"
              :href="getContactMailto()"
              target="_blank"
              rel="noopener"
              class="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            >
              <span class="inline-flex items-center gap-1.5">
                <span class="material-symbols-outlined text-base text-gray-400 hidden xl:inline">
                  open_in_new
                </span>
                {{ item.label }}
              </span>
            </a>
            <!-- Use computed property for other external links -->
            <a
              v-for="item in desktopGuestNavExternalNonContact"
              :key="item.path"
              :href="item.path"
              target="_blank"
              rel="noopener"
              class="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            >
              <span class="inline-flex items-center gap-1.5">
                <span class="material-symbols-outlined text-base text-gray-400 hidden xl:inline">
                  open_in_new
                </span>
                {{ item.label }}
              </span>
            </a>
          </template>
          <template v-else>
            <router-link
              to="/ai-youtube-video-ideas-generator"
              class="text-sm font-medium transition-colors"
              :class="isHomeActive ? 'text-primary' : 'text-gray-700 hover:text-gray-900'"
            >
              Video Idea Generator
            </router-link>
            <router-link
              to="/pricing"
              class="text-sm font-medium transition-colors"
              :class="isPricingActive ? 'text-primary' : 'text-gray-700 hover:text-gray-900'"
            >
              Pricing
            </router-link>
            <a
              href="mailto:tyteapps@gmail.com"
              target="_blank"
              rel="noopener"
              class="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            >
              <span class="inline-flex items-center gap-1.5">
                <span class="material-symbols-outlined text-base text-gray-400 hidden xl:inline">
                  mail
                </span>
                Contact
              </span>
            </a>
          </template>
        </nav>

        <!-- Desktop Auth / Profile & CTA -->
        <div class="hidden lg:flex items-center gap-3">
          <template v-if="!loggedIn">
            <router-link
              to="/login"
              class="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium
                     text-gray-700 hover:text-gray-900"
            >
              <span class="material-symbols-outlined text-base">login</span>
              Log In
            </router-link>

            <router-link
              to="/register"
              class="inline-flex items-center gap-1.5 rounded-lg bg-primary text-white px-3.5 py-2 text-sm font-medium
                     hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <span class="material-symbols-outlined text-base">rocket_launch</span>
              Get Started
            </router-link>
          </template>
          <template v-else>
            <!-- Desktop Profile with Stats -->
            <router-link to="/profile" class="flex items-center gap-3 group">
              <img
                :src="profile?.profilePicture || defaultProfilePic"
                alt="Profile"
                class="h-9 w-9 rounded-full object-cover border border-gray-200 group-hover:border-primary transition"
              />
              <div class="flex flex-col">
                <span class="text-sm font-medium text-gray-900 group-hover:text-primary transition-colors">
                  {{ generationsLeft }} left
                </span>
                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium"
                      :class="profile?.plan?.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'">
                  <span class="w-1 h-1 rounded-full" 
                        :class="profile?.plan?.active ? 'bg-green-500' : 'bg-gray-400'"></span>
                  {{ shortPlanName }}
                </span>
              </div>
            </router-link>
            <router-link
              to="/upgrade"
              class="inline-flex items-center gap-1.5 rounded-lg bg-primary text-white px-4 py-2 text-sm font-semibold shadow-sm
                     hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary transition"
            >
              <span class="material-symbols-outlined text-base">workspace_premium</span>
              Upgrade
            </router-link>
          </template>
        </div>

        <!-- Mobile Menu Button -->
        <div class="relative flex lg:hidden items-center gap-2">
          <!-- Profile Indicator (Mobile) - Only show when logged in -->
          <div v-if="loggedIn" class="flex items-center gap-2">
            <router-link to="/profile">
              <img
                :src="profile?.profilePicture || defaultProfilePic"
                alt="Profile"
                class="h-8 w-8 rounded-full object-cover border border-gray-200 shadow-sm hover:border-primary transition-colors cursor-pointer"
              />
            </router-link>
            <!-- Show generations left -->
            <div class="flex flex-col items-center">
              <span class="text-xs font-medium text-gray-600">
                {{ generationsLeft }} left
              </span>
              <span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium"
                    :class="profile?.plan?.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'">
                <span class="w-1 h-1 rounded-full" 
                      :class="profile?.plan?.active ? 'bg-green-500' : 'bg-gray-400'"></span>
                {{ shortPlanName }}
              </span>
            </div>
          </div>

          <button
            @click.stop="toggleMenu"
            aria-label="Menu"
            class="flex items-center justify-center h-10 w-10 rounded-xl bg-white border border-gray-200
                   hover:bg-gray-50 text-gray-700 hover:text-gray-900 shadow-sm
                   focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <span class="material-symbols-outlined">menu</span>
          </button>

          <!-- Mobile Dropdown - Fixed positioning -->
          <transition name="dropdown">
            <div
              v-if="isMenuOpen"
              class="absolute right-0 top-12 w-80 origin-top-right bg-white rounded-2xl shadow-xl py-4
                     ring-1 ring-black/5 z-50 border border-gray-200 overflow-hidden"
              :style="{ 
                transform: dropdownTransform,
                maxHeight: 'calc(100vh - 80px)',
                overflowY: 'auto'
              }"
              v-click-outside="closeMenu"
            >
              <!-- Profile Section (when logged in) -->
              <template v-if="loggedIn">
                <div class="px-4 py-3 border-b border-gray-100">
                  <div class="flex items-center gap-3">
                    <img
                      :src="profile?.profilePicture || defaultProfilePic"
                      alt="Profile Picture"
                      class="h-14 w-14 rounded-full object-cover border-2 border-white shadow-md"
                    />
                    <div class="flex-1 min-w-0">
                      <p class="text-base font-semibold text-gray-900 truncate">
                        {{ profile?.name }} {{ profile?.surname }}
                      </p>
                      <p class="text-sm text-gray-500 truncate">{{ profile?.email }}</p>
                    </div>
                  </div>
                  <!-- Account type badge -->
                  <div class="mt-3 flex items-center gap-2">
                    <span class="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary">
                      <span class="material-symbols-outlined text-xs">workspace_premium</span>
                      {{ profile?.plan?.name || 'Free' }} Plan
                    </span>
                    <span v-if="profile?.plan?.active" class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                      <span class="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                      Active
                    </span>
                  </div>
                </div>

                <!-- Menu Items -->
                <div class="py-2">
                  <div class="px-3 py-2 text-xs font-medium text-gray-500 uppercase">Menu</div>
                  
                  <router-link 
                    to="/ai-youtube-video-ideas-generator"
                    class="flex items-center gap-3 px-4 py-3"
                    :class="isHomeActive ? 'text-primary bg-primary/5 font-semibold' : 'text-gray-700 hover:bg-gray-50'"
                    @click="closeMenu"
                  >
                    <span class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <span class="material-symbols-outlined text-primary">smart_display</span>
                    </span>
                    <span class="font-medium">Video Idea Generator</span>
                  </router-link>
                  <router-link 
                    to="/pricing"
                    class="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50"
                    @click="closeMenu"
                  >
                    <span class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <span class="material-symbols-outlined text-primary">monetization_on</span>
                    </span>
                    <span class="font-medium">Pricing</span>
                  </router-link>

                  <button 
                    @click="openContactMail"
                    class="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50"
                  >
                    <span class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <span class="material-symbols-outlined text-primary">mail</span>
                    </span>
                    <span class="font-medium">Contact Support</span>
                  </button>
                </div>

                <!-- Account Section -->
                <div class="border-t border-gray-100 py-2">
                  <div class="px-3 py-2 text-xs font-medium text-gray-500 uppercase">Account</div>
                  
                  <router-link 
                    to="/profile"
                    class="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50"
                    @click="closeMenu"
                  >
                    <span class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <span class="material-symbols-outlined text-primary">account_circle</span>
                    </span>
                    <span class="font-medium">My Profile</span>
                  </router-link>

                  <router-link 
                    to="/upgrade"
                    class="flex items-center gap-3 px-4 py-3 text-primary font-medium hover:bg-primary/5"
                    @click="closeMenu"
                  >
                    <span class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <span class="material-symbols-outlined text-primary">workspace_premium</span>
                    </span>
                    <span>Upgrade Plan</span>
                  </router-link>
                </div>
              </template>

              <!-- Guest Menu -->
              <template v-else>
                <div class="px-4 py-3 border-b border-gray-100">
                  <h3 class="text-lg font-bold text-gray-900">Welcome to TrendzTube</h3>
                  <p class="text-sm text-gray-500">Sign in to access all features</p>
                </div>

                <div class="py-2">
                  <div
                    v-for="(option, index) in mobileGuestNav"
                    :key="index"
                    class="flex items-center gap-3 px-4 py-3 cursor-pointer"
                    :class="option.path === '/ai-youtube-video-ideas-generator' && isHomeActive
                      ? 'text-primary bg-primary/5 font-semibold'
                      : 'text-gray-700 hover:bg-gray-50'"
                    @click="option.label === 'Contact' ? openContactMail() : navigateTo(option.path, option.external)"
                  >
                    <span class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <span class="material-symbols-outlined text-primary">{{ option.icon }}</span>
                    </span>
                    <span class="font-medium">{{ option.label }}</span>
                  </div>
                </div>

                <div class="border-t border-gray-100 p-4 space-y-3">
                  <button
                    @click="navigateTo('/login')"
                    class="w-full px-4 py-2.5 rounded-xl bg-primary text-white font-semibold hover:bg-primary-dark transition-colors"
                  >
                    Log In
                  </button>
                  <button
                    @click="navigateTo('/register')"
                    class="w-full px-4 py-2.5 rounded-xl border-2 border-primary text-primary font-semibold hover:bg-primary/5 transition-colors"
                  >
                    Get Started
                  </button>
                </div>
              </template>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </header>

  <!-- spacer to offset fixed header height -->
  <div class="header-space h-14 sm:h-16"></div>
</template>

<script>
import { mapState } from 'vuex';
import { logoutUser } from '@/composables/useAuth';
import defaultProfilePic from '../../assets/defaultProfile.png';
import { colors } from '@/config/theme';
import { appName } from '@/composables/useGlobalVariables.js';

export default {
  name: 'AppHeader',
  data() {
    return {
      isMenuOpen: false,
      defaultProfilePic,
      desktopGuestNav: [
        { label: 'Video Idea Generator', path: '/' },
        { label: 'Pricing', path: '/pricing' },
        { label: 'Contact', path: 'mailto:tyteapps@gmail.com', external: true }
      ],
      desktopUserNav: [
        { label: 'Upgrade', path: '/upgrade' },
        { label: 'Contact', path: 'mailto:tyteapps@gmail.com', external: true }
      ],
      mobileGuestNav: [
        { label: 'Video Idea Generator', path: '/', icon: 'smart_display' },
        { label: 'Pricing', path: '/pricing', icon: 'monetization_on' },
        { label: 'Contact', path: 'mailto:tyteapps@gmail.com', icon: 'mail', external: true }
      ],
      mobileUserNav: [
        { label: 'Profile', path: '/profile', icon: 'account_circle' },
        { label: 'Upgrade', path: '/upgrade', icon: 'workspace_premium' },
        { label: 'Contact', path: 'mailto:tyteapps@gmail.com', icon: 'mail', external: true }
      ],
      themeColors: colors
    };
  },
  computed: {
    ...mapState(['user', 'profile', 'business']),
    appName() {
      return appName;
    },
    loggedIn() {
      return !!this.user;
    },
    truncatedBusinessName() {
      const name = this.profile?.business?.businessName || this.profile?.name || 'Account';
      return name.length > 18 ? name.slice(0, 15) + '...' : name;
    },
    desktopGuestNavInternal() {
      return this.desktopGuestNav.filter((item) => !item.external);
    },
    desktopGuestNavContact() {
      // Only Contact item
      return this.desktopGuestNav.filter((item) => item.external && item.label === 'Contact');
    },
    desktopGuestNavExternalNonContact() {
      // All external items except Contact
      return this.desktopGuestNav.filter((item) => item.external && item.label !== 'Contact');
    },
    isHomeActive() {
      // Highlight for "/" (now the generator page)
      return this.$route.path === '/';
    },
    isHomeActiveSigned() {
      // No longer needed, but keep for compatibility
      return this.$route.path === '/';
    },
    isPricingActive() {
      return this.$route.path === '/pricing';
    },
    generationsLeft() {
      if (!this.profile?.plan) return 0;
      
      const planName = this.profile.plan.name?.toLowerCase() || 'free';
      let limit = 3; // Default free plan limit
      
      // Get limit from global settings
      const globalSettings = this.$store.state.globalSettings;
      if (globalSettings?.plans) {
        if (planName.includes('free')) {
          limit = globalSettings.plans.free?.generationLimit || 3;
        } else if (planName.includes('starter')) {
          limit = globalSettings.plans.trendztubeStarter?.generationLimit || 100;
        } else if (planName.includes('pro')) {
          limit = globalSettings.plans.trendztubePro?.generationLimit || 500;
        }
      }
      
      const used = this.profile.stats?.videoIdeasGenerated || 0;
      return Math.max(0, limit - used);
    },
    dropdownTransform() {
      // Ensure dropdown stays within viewport
      if (typeof window !== 'undefined') {
        const viewportWidth = window.innerWidth;
        const dropdownWidth = 320; // 80 * 4 (w-80)
        
        if (viewportWidth < dropdownWidth + 32) { // 32px margin
          return `translateX(calc(-100% + 40px))`; // Align to button
        }
      }
      return 'translateX(0)';
    },
    shortPlanName() {
      const planName = this.profile?.plan?.name || 'Free';
      // Remove "TrendzTube" prefix and "Plan" suffix to make it shorter
      return planName
        .replace(/TrendzTube\s*/i, '')
        .replace(/\s*Plan$/i, '')
        .trim() || 'Free';
    },
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    navigateTo(path, external) {
      this.closeMenu();
      if (external) {
        window.open(path, '_blank');
      } else {
        this.$router.push(path);
      }
    },
    async handleLogout() {
      try {
        await logoutUser();
        this.$router.push('/login');
      } catch (error) {
        console.error('Logout error:', error);
      }
    },
    isActive(path) {
      // legacy, used for guest nav
      return this.$route.path.startsWith(path);
    },
    isActiveExact(path) {
      // Only highlight if exact match
      return this.$route.path === path;
    },
    getContactMailto() {
      // Compose mailto with user info if logged in
      let subject = encodeURIComponent('Trendztube Support Request');
      let body = '';
      if (this.loggedIn && this.user) {
        body =
          `Hi Trendztube Support,%0D%0A` +
          `I'm ${this.user.email},%0D%0A` +
          `My plan: ${this.profile?.plan?.name || 'Unknown'}%0D%0A` +
          `Please assist me with ...%0D%0A`;
      } else {
        body =
          `Hi Trendztube,%0D%0A` +
          `I'm Tyrese, I would like assistance with ...%0D%0A`;
      }
      return `mailto:tyteapps@gmail.com?subject=${subject}&body=${body}`;
    },
    openContactMail() {
      window.open(this.getContactMailto(), '_blank');
    }
  },
  directives: {
    'click-outside': {
      mounted(el, binding) {
        el.clickOutsideEvent = function (event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.body.addEventListener('click', el.clickOutsideEvent);
      },
      unmounted(el) {
        document.body.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  }
};
</script>

<style scoped>
/* Keep only layout heights for spacer + header */
header {
  height: 56px;
}
.header-space {
  height: 56px;
}
@media (min-width: 640px) {
  header,
  .header-space {
    height: 64px;
  }
}

/* Improved dropdown positioning */
@media (max-width: 640px) {
  .relative .absolute {
    position: fixed !important;
    right: 16px !important;
    left: auto !important;
  }
}

/* Dropdown transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.18s cubic-bezier(0.2, 0.6, 0.2, 1);
}
.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.95);
}
</style>